import type { CreditNoteResponse } from '@palaccount/contracts';
import { Banknote, CheckCheck, Pencil } from 'lucide-react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useSearchParams } from 'react-router';

import { api } from '@/api';
import { Amount } from '@/components/amount';
import {
  BulkExportButton,
  BulkPdfButton,
  BulkPostButton,
  DocumentListPage,
  DocumentPdfButton,
  documentLocale,
  normalisePaged,
  type DocumentListConfig,
} from '@/components/document-list';
import { Gated } from '@/components/permission-gate';
import { StatusChip } from '@/components/status-chip';
import { Button } from '@/components/ui/button';
import { NativeSelect } from '@/components/ui/native-select';
import { useCashAccountOptions } from '@/features/accounts/api';
import { sumAmounts } from '@/lib/money';
import { denyPermission } from '@/lib/permissions';
import { ApiError, localizeServerCause, throwApiError } from '@/lib/server-messages';

import { INVOICES_KEY } from './api';
import {
  fetchCreditNotesPage,
  RETURNS_KEY,
  RETURNS_SEARCHABLE,
  useCreditNote,
  useCreditNotes,
  useCreditNoteStats,
  usePostCreditNote,
  useRefundCreditNote,
} from './returns-api';
import { creditNoteSteps } from './sales-steppers';
import { SalesTabs } from './sales-tabs';

const STATUSES: CreditNoteResponse['status'][] = ['draft', 'posted', 'cancelled'];
const TONES = { draft: 'draft', posted: 'posted', credited: 'completed', cancelled: 'cancelled' } as const;

type CreditNoteStats = ReturnType<typeof useCreditNoteStats>['data'] & object;

const trimQty = (qty: string) => qty.replace(/\.?0+$/, '');

/**
 * SAL-05's list on the shared list-and-detail page (wave 3B): the credit
 * note's OWN number (BR-SAL-05-09), the invoice it is against as a chip (or
 * none — RET-02's stand-alone return), the register's four tiles, and the
 * return's own chain — Invoice → Return → Stock back → Credit note / refund,
 * greyed where it does not apply (damaged goods never restock, BR-SAL-05-07).
 * The acts are the editor's: post a draft, refund a posted note in cash
 * (SAL-05 §15 Q3, the treasurer's permission), print it. The New button
 * still opens the STAND-ALONE editor (owner decision 2026-10-12), and the
 * empty state still says both ways in.
 */
export function ReturnsListPage() {
  const { t, i18n } = useTranslation();
  const locale = documentLocale(i18n.language);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [refundAccountId, setRefundAccountId] = useState('');
  const { options: cashOptions } = useCashAccountOptions();
  const [params] = useSearchParams();
  const selectedId = params.get('selected') ?? '';
  const postCreditNote = usePostCreditNote(selectedId);
  const refundCreditNote = useRefundCreditNote(selectedId);

  const act = async (action: () => Promise<unknown>) => {
    setBusy(true);
    setError(null);
    try {
      await action();
    } catch (thrown) {
      if (thrown instanceof ApiError && thrown.status === 403) denyPermission('sales.approve');
      setError(localizeServerCause(t, thrown, 'doclist.actFailed'));
    } finally {
      setBusy(false);
    }
  };

  const statusLabel = (status: CreditNoteResponse['status']) => t(`common.status.${status}`);
  const chip = (note: CreditNoteResponse) => <StatusChip status={note.status} tones={TONES} ns="common" />;
  const exportColumns = [
    { header: t('returns.colNo'), value: (n: CreditNoteResponse) => n.code },
    { header: t('common.date'), value: (n: CreditNoteResponse) => n.creditDate, kind: 'date' as const },
    { header: t('returns.colAgainst'), value: (n: CreditNoteResponse) => n.invoiceCode ?? '' },
    { header: t('sales.colCustomer'), value: (n: CreditNoteResponse) => n.partnerName },
    { header: t('returns.colReason'), value: (n: CreditNoteResponse) => t(`returns.reason.${n.reason}`) },
    { header: t('sales.colStatus'), value: (n: CreditNoteResponse) => statusLabel(n.status) },
    { header: t('sales.exportColCurrency'), value: (n: CreditNoteResponse) => n.currency },
    { header: t('returns.colCredit'), value: (n: CreditNoteResponse) => n.grandTotal, kind: 'number' as const },
  ];
  const pdfRequest = (note: CreditNoteResponse) => ({ kind: 'credit_note' as const, locale, subjectId: note.id });
  const toFilters = (filters: { status: CreditNoteResponse['status'] | ''; dateFrom: string; dateTo: string; partnerId: string }) => ({
    status: filters.status || undefined,
    dateFrom: filters.dateFrom || undefined,
    dateTo: filters.dateTo || undefined,
    partnerId: filters.partnerId || undefined,
  });

  const config: DocumentListConfig<CreditNoteResponse, CreditNoteResponse['status'], CreditNoteStats> = {
    titleKey: 'returns.title',
    tabs: <SalesTabs />,
    /* RET-02: a return without a source invoice starts here. */
    newAction: { to: '/sales/returns/new', labelKey: 'common.new', permission: 'sales.create', reasonKey: 'perm.cannot.createSalesReturns' },
    statuses: STATUSES,
    statusLabel,
    search: RETURNS_SEARCHABLE ? { labelKey: 'doclist.searchLabel', placeholderKey: 'doclist.searchPlaceholder' } : undefined,
    dateRange: true,
    partner: { kind: 'customer' },
    useList: (filters, page, pageSize) => {
      const query = useCreditNotes(toFilters(filters), page, pageSize);
      return { data: normalisePaged(query.data, 'creditNotes'), isPending: query.isPending, isError: query.isError };
    },
    useById: (id) => useCreditNote(id ?? undefined),
    kpis: {
      useStats: () => {
        const stats = useCreditNoteStats();
        return { data: stats.data, isPending: stats.isPending, isError: stats.isError };
      },
      tiles: [
        { key: 'monthValue', labelKey: 'returns.tileMonthValue', format: 'money', value: (s) => s.returnsThisMonthValue, currency: (s) => s.currency },
        { key: 'monthCount', labelKey: 'returns.tileMonthCount', format: 'count', value: (s) => s.returnsThisMonthCount },
        { key: 'awaiting', labelKey: 'returns.tileAwaiting', format: 'count', value: (s) => s.awaitingCreditOrRefund, tone: 'warn' },
        { key: 'rate', labelKey: 'returns.tileRate', format: 'pct', value: (s) => s.returnRatePct },
      ],
    },
    card: (note) => ({
      code: note.code,
      chip: chip(note),
      title: note.partnerName,
      meta: (
        <>
          <bdi dir="ltr">{note.creditDate}</bdi> · {t(`returns.reason.${note.reason}`)}
        </>
      ),
      amount: { value: note.grandTotal, currency: note.currency },
    }),
    columns: { titleKey: 'sales.colCustomer', metaKey: 'returns.colReason', amountKey: 'returns.colCredit', statusKey: 'sales.colStatus' },
    emptyKey: 'returns.empty',
    selectOneKey: 'returns.selectOne',
    notice: error ? (
      <p role="alert" className="rounded-md border border-destructive/40 bg-destructive/10 px-4 py-3 text-sm text-destructive">
        {error}
      </p>
    ) : null,
    export: {
      filename: 'sales-returns',
      columns: exportColumns,
      fetchPage: (filters, page, pageSize) => fetchCreditNotesPage(toFilters(filters), page, pageSize),
    },
    bulkActions: (rows, clear) => (
      <>
        <BulkPostButton
          rows={rows}
          permission="sales.approve"
          reasonKey="perm.cannot.postSalesReturns"
          invalidate={[RETURNS_KEY, INVOICES_KEY]}
          clear={clear}
          post={async (row) => {
            const response = await api.creditNotes.post({ params: { creditNoteId: row.id }, body: {} });
            if (response.status !== 200) throwApiError(response.status, response.body);
          }}
        />
        <BulkPdfButton requests={rows.map(pdfRequest)} />
        <BulkExportButton filename="sales-returns" columns={exportColumns} rows={rows} />
      </>
    ),
    detail: {
      chip,
      links: (note) =>
        note.invoiceCode
          ? [
              {
                key: 'invoice',
                to: `/sales/${note.invoiceId}`,
                tone: 'muted',
                label: t('returns.againstInvoice', { code: note.invoiceCode }),
              },
            ]
          : [],
      subline: (note) => (
        <>
          {note.partnerName} · {t(`returns.reason.${note.reason}`)}
          {note.reasonNote ? ` — ${note.reasonNote}` : null}
        </>
      ),
      steps: { labelKey: 'returns.stepper', of: creditNoteSteps },
      /* "qty returned / invoiced" — only a linked return has an invoiced quantity to compare with. */
      progress: (note) => {
        if (note.invoiceId === null) return null;
        const returned = sumAmounts(note.lines.map((line) => line.qty));
        const invoiced = sumAmounts(note.lines.map((line) => line.billedQty));
        return t('returns.listProgress', { returned: trimQty(returned), invoiced: trimQty(invoiced) });
      },
      actions: (note) => (
        <>
          <Button asChild variant="outline">
            <Link to={`/sales/returns/${note.id}`}>
              <Pencil aria-hidden /> {note.status === 'draft' ? t('common.edit') : t('doclist.open')}
            </Link>
          </Button>
          {note.status === 'draft' ? (
            <Gated permission="sales.approve" reasonKey="perm.cannot.postSalesReturns">
              <Button onClick={() => void act(() => postCreditNote.mutateAsync({}))} disabled={busy}>
                <CheckCheck aria-hidden /> {t('returns.actPost')}
              </Button>
            </Gated>
          ) : null}
          <DocumentPdfButton request={pdfRequest(note)} />
          {note.status === 'posted' && note.refundedAt === null ? (
            <>
              <NativeSelect
                aria-label={t('returns.refundAccount')}
                value={refundAccountId}
                onChange={(event) => setRefundAccountId(event.target.value)}
                className="h-10 w-auto text-sm"
              >
                <option value="">{t('receipts.pickAccount')}</option>
                {cashOptions.map((option) => (
                  <option key={option.id} value={option.id}>
                    {option.code} — {option.name}
                  </option>
                ))}
              </NativeSelect>
              <Gated permission="accounting.approve" reasonKey="perm.cannot.refundCreditNotes">
                <Button
                  variant="outline"
                  disabled={busy || !refundAccountId}
                  onClick={() => void act(() => refundCreditNote.mutateAsync(refundAccountId))}
                >
                  <Banknote aria-hidden /> {t('returns.refund')}
                </Button>
              </Gated>
            </>
          ) : null}
        </>
      ),
      lines: {
        columns: [
          { key: 'item', labelKey: 'quotes.colItem' },
          { key: 'qty', labelKey: 'sales.colQty', align: 'end', className: 'w-24' },
          { key: 'unitPrice', labelKey: 'sales.colUnitPrice', align: 'end', className: 'w-32' },
          { key: 'total', labelKey: 'sales.colTotal', align: 'end', className: 'w-32' },
        ],
        rows: (note) =>
          note.lines.map((line) => ({
            key: String(line.lineNo),
            cells: [
              <span key="d" className="font-medium">{line.description}</span>,
              <bdi key="q" dir="ltr" className="font-mono">{trimQty(line.qty)}</bdi>,
              <Amount key="u" value={line.unitPrice} currency={note.currency} />,
              <Amount key="t" value={line.lineTotal} currency={note.currency} />,
            ],
          })),
      },
      totals: (note) => ({
        labelKey: 'orders.netTaxTotal',
        amounts: [
          { value: note.subtotal, currency: note.currency },
          { value: note.taxAmount, currency: note.currency },
          { value: note.grandTotal, currency: note.currency, emphasis: true },
        ],
      }),
    },
  };

  return <DocumentListPage config={config} />;
}
